import { Component, OnInit } from '@angular/core';
import { SidebarStateService } from '../../../services/sidebar-state/sidebar-state.service';
import { CommonModule } from '@angular/common';
import { UserInfoComponent } from '../../units/zikata-user-info/zikata-user-info.component';
import IZikataClientProfile, {
  UserInfoConfigService,
  UserRole,
} from '../../../services/user-info-config/user-info-config.service';

@Component({
  selector: 'zikatagov-header',
  standalone: true,
  imports: [CommonModule, UserInfoComponent],
  template: `
    <header class="app-header">
      <div class="header-inner">

        <!-- LEFT SIDE -->
        <div class="header-left">
          
          <div class="header-user">
            <zikatagov-user-info
              [userProfile]="userProfile"
              [userRole]="rolesString"
              [avatarSize]="36"
            ></zikatagov-user-info>
          </div>
        </div>

        <!-- RIGHT SIDE -->
      
      </div>

    </header>
  `,
  styles: [`
    :host {
      display: block;
      font-family: 'DM Sans', 'Segoe UI', sans-serif;
    }

    /* ── Header shell ── */
    .app-header {
      position: sticky;
      top: 0;
      z-index: 100;
      background: #ffffff;
      border-bottom: 1.5px solid #f0eafa;
      box-shadow: 0 2px 20px rgba(109, 40, 217, 0.06);
    }

    /* ── Main layout ── */
    .header-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 80px;
      padding: 0 2rem;
      max-width: 1920px;
      margin: 0 auto;
    }

    /* ── Left group ── */
    .header-left {
      display: flex;
      align-items: center;
      gap: 1.5rem;
      flex: 1;
      min-width: 0; /* Allow flex items to shrink */
    }



    /* ── User section ── */
    .header-user {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0; /* Allow content to shrink properly */
    }

    /* ── Right side logo ── */
    .header-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-shrink: 0;
      padding-left: 2rem;
    }

    .header-logo {
      height: 44px;
      width: auto;
      object-fit: contain;
    }



    /* ── Color overrides for user-info ── */
    :host ::ng-deep .text-white,
    :host ::ng-deep .text-light {
      color: #1a003d !important;
    }

    :host ::ng-deep small.font-weight-light,
    :host ::ng-deep small {
      color: #9ca3af !important;
    }

    :host ::ng-deep .vr.bg-white {
      background: #e5e7eb !important;
    }

    :host ::ng-deep ng-icon.text-white {
      color: #7c3aed !important;
    }

    :host ::ng-deep .btn-outline-light {
      border-color: #ede9fb !important;
      color: #374151 !important;
      background: #faf8ff !important;
    }

    :host ::ng-deep .btn-outline-light:hover {
      background: #f3eeff !important;
      border-color: #c4b5fd !important;
      color: #7c3aed !important;
    }

    :host ::ng-deep .mobile-menu {
      right: 1rem;
      top: calc(100% + 4px);
      background: #ffffff;
      border: 1.5px solid #f0eafa;
      box-shadow: 0 8px 32px rgba(109, 40, 217, 0.12);
      border-radius: 12px;
    }

    /* ── Mobile Responsive ── */
    @media (max-width: 1280px) {
      .header-inner {
        padding: 0 1.5rem;
      }

      .header-left {
        gap: 1.25rem;
      }

      .header-right {
        padding-left: 1.5rem;
      }

      .header-logo {
        height: 40px;
      }
    }

    @media (max-width: 1024px) {
      .header-inner {
        padding: 0 1.25rem;
        height: 76px;
      }

      .header-left {
        gap: 1rem;
      }

      .header-right {
        padding-left: 1.25rem;
      }
    }

    @media (max-width: 768px) {
      .header-inner {
        padding: 0 1rem;
        height: 72px;
      }

      .header-left {
        gap: 0.875rem;
      }

      .header-accent-bar {
        width: 36px;
        height: 4px;
      }

      .header-right {
        padding-left: 1rem;
      }

      .header-logo {
        height: 36px;
      }
    }

    @media (max-width: 480px) {
      .header-inner {
        padding: 0 0.75rem;
        height: 68px;
      }

      .header-left {
        gap: 0.75rem;
      }

      .header-accent-bar {
        width: 32px;
      }

      .header-right {
        padding-left: 0.75rem;
      }

      .header-logo {
        height: 32px;
      }
    }
  `],
})
export class HeaderComponent implements OnInit {
  fullName = '';
  rolesString = '';
  isCollapsed = false;
  userProfile?: IZikataClientProfile;

  constructor(
    private sidebarStateService: SidebarStateService,
    private userInfoConfigService: UserInfoConfigService
  ) {}

  ngOnInit() {
    this.sidebarStateService.isCollapsed$.subscribe(
      (isCollapsed) => (this.isCollapsed = isCollapsed)
    );
    this.loadUserAccount();
  }

  private loadUserAccount() {
    this.userInfoConfigService.getUserAccount().subscribe({
      next: (profile) => {
        this.userProfile = profile;
        this.formatUserInfo(profile);
      },
      error: (error) => {
        console.error('Error loading user account:', error);
        this.fullName = 'Guest User';
        this.rolesString = 'Unknown Role';
      },
    });
  }

  private formatUserInfo(profile: IZikataClientProfile) {
    this.fullName = this.formatFullName(profile);
    this.rolesString = this.formatRoles(profile.userRoles);
  }

  private formatFullName(profile: IZikataClientProfile): string {
    return [profile.firstName, profile.middleName, profile.lastName]
      .filter(Boolean)
      .join(' ');
  }

  private formatRoles(roles: UserRole[]): string {
    if (!roles || roles.length === 0) return 'No Role';
    return roles.map((role) => role.role).join(', ');
  }
}